/**
 * Countdown days, hours, minutes and seconds
 * @module countdown
 */

  // Styleguide documenation URL: /fw/fw_src/mobile/styleguide/site/modules.html#countdown

import $ from 'jquery';
import registerJQueryPlugin from '../lib/register-jquery-plugin';
import mergeOptions from '../lib/merge-options';

export const SECOND = 1000;
export const MINUTE = SECOND * 60;
export const HOUR   = MINUTE * 60;
export const DAY    = HOUR * 24;

export const DAY_CONTAINER = '.js-countdown-day';
export const HOURS_AS_DAYS_CONTAINER = '.js-countdown-hours-as-days';
export const HOUR_CONTAINER = '.js-countdown-hour';
export const MINUTE_CONTAINER = '.js-countdown-minute';
export const SECOND_CONTAINER = '.js-countdown-second';
export const DAY_LABEL = '.js-countdown-day-label';
export const HOURS_AS_DAYS_LABEL = '.js-countdown-hours-as-days-label';
export const HOUR_LABEL = '.js-countdown-hour-label';
export const MINUTE_LABEL = '.js-countdown-minute-label';
export const SECOND_LABEL = '.js-countdown-second-label';

// Expose the function as a jQuery plugin for ease of use
export const PLUGIN_NAME = 'countdown';
export const DEFAULTS = {
  targetDateTime:   '2029-12-01T00:00:00-08:00',
  countdownEndText: null,
  countdownRemove: false,
};
/**
 * Update countdown heading or remove when time expires
 * @param  {jQuery} $el - link
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
 const updateCountdownHeading = ($el, opts) => {
   // Get the countdown heading
   const $countdownHed = $el.find('.js-countdown__hed');
   // Get data-countdown-end-text value - must be set on parent
   const countdownEndText = opts.countdownEndText;
   // If countdown heading exists and there is a countdownEndText value
   if($countdownHed.length && countdownEndText !== null) {
       $countdownHed.text(countdownEndText);
   }
 }
/**
 * Boolean to see if seconds should be displayed
 * @param  {Object} [opts] - Any options provided upon initialization
 */
 const getThreshold = (opts) => !opts.displaySeconds ? 60 : 0;
/**
 * Boolean to see if time has expired
 * @param  {Object} [opts] - Any options provided upon initialization
 */
 const initialTimeCheck = (target, opts) => {
   const now = new Date().getTime();
   const initialTimeLeft = target - now;
   const initialSecondsRemaining = Math.floor(initialTimeLeft / 1000);
   // Check it time remaining is more than the thershold
   return initialSecondsRemaining > getThreshold(opts);
 }
 /**
 * Creates an object to cach countdown elements
 * @param  {jQuery} $el - link
 */
 const queryElements = ($el) => ({
   dayContainer: $el.find(DAY_CONTAINER),
   hourContainer: $el.find(HOUR_CONTAINER),
   hourAsDaysContainer: $el.find(HOURS_AS_DAYS_CONTAINER),
   minuteContainer: $el.find(MINUTE_CONTAINER),
   secondContainer: $el.find(SECOND_CONTAINER),
   dayLabel: $el.find(DAY_LABEL),
   hourLabel: $el.find(HOUR_LABEL),
   hourAsDaysLabel: $el.find(HOURS_AS_DAYS_LABEL),
   minuteLabel: $el.find(MINUTE_LABEL),
   secondLabel: $el.find(SECOND_LABEL),
 });
 /**
  * Updates the countdown display elements
  * @param  {jQuery} $el - link
  * @param  {Object} [options={}] - Any options provided upon initialization
  * @param  {number} timeLeft - amount of time still left in the countdown
  */
 const updateCountdownDisplay = ($el, $elements, opts, timeLeft) => {
   const daysLeft = Math.floor(timeLeft / (DAY));
   const daysLeftCheck = daysLeft === 0 ? daysLeft : (daysLeft * 24);
   let hoursDaysLeft = Math.floor(((timeLeft % DAY) / (HOUR)) + (daysLeftCheck));
   hoursDaysLeft = hoursDaysLeft < 10 ? (`00${hoursDaysLeft}`).slice(-2) : hoursDaysLeft;
   const hoursLeft = (`00${Math.floor((timeLeft % (DAY)) / (HOUR))}`).slice(-2);
   const minutesLeft = (`00${Math.floor((timeLeft % (HOUR)) / (MINUTE))}`).slice(-2);
   const secondsLeft = (`00${Math.floor((timeLeft % (MINUTE)) / (SECOND))}`).slice(-2);

   $elements.dayContainer.text(daysLeft);
   $elements.hourContainer.text(hoursLeft);
   $elements.hourAsDaysContainer.text(hoursDaysLeft);
   $elements.minuteContainer.text(minutesLeft);
   $elements.secondContainer.text(secondsLeft);

   $elements.dayLabel.text((daysLeft === 1 ? 'day' : 'days'));
   $elements.hourLabel.text((hoursLeft === '01' ? 'hour' : 'hours'));
   $elements.hourAsDaysLabel.text((parseInt(hoursDaysLeft) === 1 ? 'hour' : 'hours'));
   $elements.minuteLabel.text((minutesLeft === '01' ? 'minute' : 'minutes'));
   $elements.secondLabel.text((secondsLeft === '01' ? 'second' : 'seconds'))

 }
/**
 * Runs the countdown and sets the interval
 * @param  {jQuery} $el - link
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
 const runCountdown = ($el, $elements, opts) => {
   let target = new Date(opts.targetDateTime).getTime();
   // initial check to see if any time remains
   const timeRemains = initialTimeCheck(target, opts);
   // if no time remains
   if(!timeRemains){
     // Check if countdown should be removed otherwise zero it out
     if (opts.countdownRemove) {
       $el.children().not('.js-countdown__hed').remove();
     } else {
       updateCountdownDisplay($el, $elements, opts, 0);
     }
     // Display end text if provided
     updateCountdownHeading($el, opts);
     // Show countdown markup
     $el.removeClass('u-hide');
     return;
   }

   $el.removeClass('u-hide');

   let intervalId = setInterval(() => {
     let now = new Date().getTime();
     const timeLeft = target - now;
     let secondsRemaining = Math.floor(timeLeft / 1000);
     // Check if countdown has expired
     if (secondsRemaining <= getThreshold(opts)) {
         clearInterval(intervalId);
         updateCountdownDisplay($el, $elements, opts, 0);
         // Display end text if provided
         updateCountdownHeading($el, opts);
         // Remove countdown elements if option is enabled
         if (opts.countdownRemove) {
           $el.children().not('.js-countdown__hed').remove();
         }
         return;
     }

     updateCountdownDisplay($el, $elements, opts, timeLeft);
   }, SECOND);
 }
/**
 * Initializes countdown
 * @param {HTMLElement} el - link
 * @param {Object} options - Options used for wiring up the modal
 * @see DEFAULTS
 */
export default function countdown(el, options = {}) {
  const $el = $(el);
  const opts = mergeOptions(DEFAULTS, options, $el);
  // Check boolean options on initialization for countdown removal
  opts.countdownRemove = opts.countdownRemove === true || opts.countdownRemove === "";
  const $elements = queryElements($el);
  // Check if seconds markup is present
  opts.displaySeconds = $el.find('.js-countdown-second').length;
  // Check if countdown is hidden
  opts.countdownHidden = $el.hasClass('u-hide');
  runCountdown($el, $elements, opts);
}
registerJQueryPlugin(PLUGIN_NAME, countdown);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/countdown.js